import { createFileRoute } from "@tanstack/react-router";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Users as UsersIcon,
  Mail,
  Phone,
  ChevronRight,
  Link2,
  Building2,
} from "lucide-react";
import { ClientFormDialog, type ClientRow } from "@/components/clients/ClientFormDialog";
import { ClientDocumentsRow } from "@/components/clients/ClientDocumentsRow";
import { ClientInvitesDialog } from "@/components/clients/ClientInvitesDialog";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/clients")({
  component: ClientsPage,
});

function ClientsPage() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"active" | "inactive" | "all">("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ClientRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClientRow | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [invitesOpen, setInvitesOpen] = useState(false);
  const isAdmin = hasRole("admin");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("clients")
      .select("*")
      .order("name", { ascending: true });
    if (error) {
      toast.error(t("clients.loadFailed"), { description: error.message });
    } else {
      setClients((data ?? []) as ClientRow[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scopedClients = useFilterByOwnerCompany(clients);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = scopedClients;
    if (statusFilter === "active") list = list.filter((c) => c.is_active);
    else if (statusFilter === "inactive") list = list.filter((c) => !c.is_active);
    if (q) {
      list = list.filter((c) =>
        [c.name, c.company_name, c.email, c.phone, c.city, c.state]
          .filter(Boolean)
          .some((v) => v!.toLowerCase().includes(q)),
      );
    }
    // Ativos sempre em evidência: ordena ativos primeiro, depois por nome.
    return [...list].sort(
      (a, b) => Number(b.is_active) - Number(a.is_active) || a.name.localeCompare(b.name),
    );
  }, [scopedClients, search, statusFilter]);

  const onCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const onEdit = (c: ClientRow) => {
    setEditing(c);
    setDialogOpen(true);
  };

  const onDelete = async () => {
    if (!deleteTarget) return;
    const clientId = deleteTarget.id;

    const tryDelete = async () => await supabase.from("clients").delete().eq("id", clientId);

    let { error } = await tryDelete();

    // Se o bloqueio for por projetos órfãos (sem dependências), apaga-os e tenta de novo
    if (
      error &&
      ((error as { code?: string }).code === "23503" || /foreign key/i.test(error.message)) &&
      /projects/i.test(error.message)
    ) {
      const { data: projs } = await supabase
        .from("projects")
        .select("id")
        .eq("client_id", clientId);
      const projectIds = (projs ?? []).map((p) => p.id);
      if (projectIds.length > 0) {
        const { error: projDelErr } = await supabase.from("projects").delete().in("id", projectIds);
        if (!projDelErr) {
          const retry = await tryDelete();
          error = retry.error;
        }
      }
    }

    if (error) {
      const isFk =
        (error as { code?: string }).code === "23503" || /foreign key/i.test(error.message);
      if (isFk) {
        const { error: updErr } = await supabase
          .from("clients")
          .update({ is_active: false })
          .eq("id", clientId);
        if (updErr) {
          toast.error(t("clients.deleteFailed"), { description: updErr.message });
        } else {
          toast.warning(
            "Cliente possui registros vinculados (orçamentos, contratos, faturas ou projetos com horas/despesas) e não pode ser excluído. Foi desativado.",
          );
          setClients((prev) =>
            prev.map((c) => (c.id === clientId ? { ...c, is_active: false } : c)),
          );
        }
      } else {
        toast.error(t("clients.deleteFailed"), { description: error.message });
      }
    } else {
      toast.success(t("clients.deleted"));
      setClients((prev) => prev.filter((c) => c.id !== clientId));
    }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("clients.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("clients.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <Button variant="outline" onClick={() => setInvitesOpen(true)} className="gap-2">
              <Link2 className="h-4 w-4" />
              Gerar link de cadastro
            </Button>
          )}
          <Button onClick={onCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            {t("clients.newClient")}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 border-b border-border p-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("clients.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 border-0 bg-transparent shadow-none focus-visible:ring-0"
          />
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as "active" | "inactive" | "all")}
          >
            <SelectTrigger className="ml-auto h-8 w-[130px] shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">
                {t("common.active", { defaultValue: "Ativos" })}
              </SelectItem>
              <SelectItem value="inactive">
                {t("common.inactive", { defaultValue: "Inativos" })}
              </SelectItem>
              <SelectItem value="all">{t("common.all", { defaultValue: "Todos" })}</SelectItem>
            </SelectContent>
          </Select>
          <Badge variant="secondary" className="shrink-0">
            {t("clients.count", { count: filtered.length })}
          </Badge>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-12">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState hasAny={clients.length > 0} onCreate={onCreate} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[1%]" />
                  <TableHead>{t("clients.name")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("clients.company")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("clients.contact")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("clients.location")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((c) => {
                  const isExpanded = expandedId === c.id;
                  return (
                    <Fragment key={c.id}>
                      <TableRow
                        className="cursor-pointer"
                        onClick={() => setExpandedId(isExpanded ? null : c.id)}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => setExpandedId(isExpanded ? null : c.id)}
                            aria-label={isExpanded ? "Recolher" : "Expandir"}
                          >
                            <ChevronRight
                              className={cn(
                                "h-4 w-4 transition-transform",
                                isExpanded && "rotate-90",
                              )}
                            />
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <CompanyBadge companyId={c.company_id} />
                            <div className="flex flex-col">
                              <span>{c.name}</span>
                              {c.company_name && (
                                <span className="flex items-center gap-1 text-xs text-muted-foreground sm:hidden">
                                  <Building2 className="h-3 w-3" />
                                  {c.company_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell text-sm">
                          {c.company_name ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2.5 py-0.5 font-medium text-primary">
                              <Building2 className="h-3.5 w-3.5" />
                              {c.company_name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="space-y-0.5 text-sm">
                            {c.email && (
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <Mail className="h-3 w-3" />
                                {c.email}
                              </div>
                            )}
                            {c.phone && (
                              <div className="flex items-center gap-1.5 text-muted-foreground">
                                <Phone className="h-3 w-3" />
                                {c.phone}
                              </div>
                            )}
                            {!c.email && !c.phone && (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                          {[c.city, c.state].filter(Boolean).join(", ") || "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={c.is_active ? "default" : "secondary"}>
                            {c.is_active ? t("common.active") : t("common.inactive")}
                          </Badge>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => onEdit(c)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setDeleteTarget(c)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow key={`${c.id}-details`} className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={6} className="p-0">
                            <ClientDocumentsRow clientId={c.id} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ClientFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        client={editing}
        onSaved={(saved, mode) => {
          if (mode === "create") {
            setClients((prev) => [saved, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
          } else {
            setClients((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
          }
        }}
      />

      <ClientInvitesDialog open={invitesOpen} onOpenChange={setInvitesOpen} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("clients.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <Trans
                  i18nKey="clients.deleteConfirmDesc"
                  values={{ name: deleteTarget?.name ?? "" }}
                  components={{ strong: <strong /> }}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EmptyState({ hasAny, onCreate }: { hasAny: boolean; onCreate: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center gap-3 p-12 text-center">
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full text-primary-foreground"
        style={{ background: "var(--gradient-brand)" }}
      >
        <UsersIcon className="h-6 w-6" />
      </div>
      <div>
        <p className="font-medium">
          {hasAny ? t("clients.noClientsMatch") : t("clients.noClientsYet")}
        </p>
        <p className="text-sm text-muted-foreground">
          {hasAny ? t("clients.tryDifferent") : t("clients.addFirst")}
        </p>
      </div>
      {!hasAny && (
        <Button onClick={onCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          {t("clients.addClient")}
        </Button>
      )}
    </div>
  );
}
